import React, { useEffect, useState } from 'react';
import { TwilioScript, fetchExotelScripts, createExotelScript, updateExotelScript, deleteExotelScript } from '../utils/api';
import PromptEditor from './PromptEditor';
import './ScriptTemplates.css';

const VOICES = ['Aoede', 'Despina', 'Sulafat', 'Kore', 'Autonoe'];
const EMPTY_SCRIPT_FORM: Omit<TwilioScript, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  systemPrompt: '',
  context: '',
  greetingMessage: 'Hello! Thank you for calling. How can I help you today?',
  voice: 'Kore',
  isDefault: false,
};

interface ScriptTemplatesProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const ScriptTemplates: React.FC<ScriptTemplatesProps> = ({ userRole }) => {
  const [scripts, setScripts] = useState<TwilioScript[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingScriptId, setEditingScriptId] = useState<string | null>(null);
  const [scriptForm, setScriptForm] = useState({ ...EMPTY_SCRIPT_FORM });
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [isWideView, setIsWideView] = useState(false);

  const loadScriptsList = async () => {
    setLoading(true);
    try {
      const list = await fetchExotelScripts();
      setScripts(list);
    } catch (err) {
      console.error('Failed to load scripts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScriptsList();
  }, []);

  const startScriptEdit = (s: TwilioScript) => {
    setEditingScriptId(s.id);
    setScriptForm({
      name: s.name,
      systemPrompt: s.systemPrompt,
      context: s.context || '',
      greetingMessage: s.greetingMessage || '',
      voice: s.voice || 'Aoede',
      isDefault: s.isDefault || false,
    });
    setFormError('');
    setIsWideView(false);
  };

  const startScriptCreate = () => {
    setEditingScriptId('__new__');
    setScriptForm({ ...EMPTY_SCRIPT_FORM });
    setFormError('');
    setIsWideView(false);
  };

  const saveScript = async () => {
    if (!scriptForm.name.trim() || !scriptForm.systemPrompt.trim()) {
      setFormError('Name and System Prompt are required.');
      return;
    }
    setFormSaving(true);
    setFormError('');
    try {
      if (editingScriptId === '__new__') {
        await createExotelScript(scriptForm);
      } else if (editingScriptId) {
        await updateExotelScript(editingScriptId, scriptForm);
      }
      setEditingScriptId(null);
      setIsWideView(false);
      await loadScriptsList();
    } catch (e: any) {
      setFormError(e.message || 'Save failed');
    } finally {
      setFormSaving(false);
    }
  };

  const deleteScript = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this script template?')) return;
    try {
      await deleteExotelScript(id);
      await loadScriptsList();
      if (editingScriptId === id) setEditingScriptId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className={`scripts-view-container${isWideView && editingScriptId ? ' wide-view' : ''}`}>
      {/* Left List Panel */}
      <div className="scripts-list-panel">
        <div className="panel-title-bar">
          <h4>Script Templates ({scripts.length})</h4>
          <button className="add-template-btn" onClick={startScriptCreate}>
            + New Template
          </button>
        </div>

        {loading ? (
          <div className="panel-empty-state">Loading templates...</div>
        ) : scripts.length === 0 ? (
          <div className="panel-empty-state">No templates created yet. Click New Template to start.</div>
        ) : (
          <ul className="template-list-items">
            {scripts.map((s) => (
              <li
                key={s.id}
                className={`template-list-item-card${editingScriptId === s.id ? ' active' : ''}`}
                onClick={() => startScriptEdit(s)}
              >
                <div className="template-item-details">
                  <div className="template-item-name-row">
                    <strong>{s.name}</strong>
                    {s.isDefault && <span className="default-badge-label">Default</span>}
                  </div>
                  <span className="template-item-meta">
                    Voice: {s.voice} · Last updated: {new Date(s.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <button className="delete-item-action-btn" onClick={(e) => deleteScript(s.id, e)}>
                  🗑️
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right Form Panel */}
      <div className="scripts-form-panel">
        <div className="panel-title-bar">
          <h4>
            {editingScriptId === '__new__'
              ? 'New Script Template'
              : editingScriptId
                ? 'Edit Script Template'
                : 'Select a Template'}
          </h4>
          {editingScriptId && (
            <button
              type="button"
              className="toggle-wide-btn"
              onClick={() => setIsWideView(!isWideView)}
            >
              {isWideView ? '🗗 Split Screen' : '🗖 Broad Screen'}
            </button>
          )}
        </div>

        {editingScriptId ? (
          <div className="template-form-body">
            <div className="form-group-custom">
              <label>Template Name *</label>
              <input
                type="text"
                value={scriptForm.name}
                onChange={(e) => setScriptForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Sales Follow-up Script"
                className="form-input-field"
              />
            </div>

            <div className="form-group-custom">
              <label>System Prompt *</label>
              <PromptEditor
                value={scriptForm.systemPrompt}
                onChange={(val) => setScriptForm((f) => ({ ...f, systemPrompt: val }))}
                placeholder="Define AI behaviors, agent rules, scope limits, and details..."
                height={isWideView ? '340px' : '220px'}
              />
            </div>

            <div className="form-group-custom">
              <label>Knowledge Context / FAQs</label>
              <PromptEditor
                value={scriptForm.context}
                onChange={(val) => setScriptForm((f) => ({ ...f, context: val }))}
                placeholder="Provide details about product prices, policies, or context answers..."
                height={isWideView ? '200px' : '120px'}
              />
            </div>

            <div className="form-group-custom">
              <label>Greeting Message</label>
              <input
                type="text"
                value={scriptForm.greetingMessage}
                onChange={(e) => setScriptForm((f) => ({ ...f, greetingMessage: e.target.value }))}
                placeholder="Message spoken immediately when call connects..."
                className="form-input-field"
              />
            </div>

            <div className="form-row-custom">
              <div className="form-group-custom half">
                <label>AI Voice Model</label>
                <select
                  value={scriptForm.voice}
                  onChange={(e) => setScriptForm((f) => ({ ...f, voice: e.target.value }))}
                  className="form-select-field"
                >
                  {VOICES.map((v) => (
                    <option key={v} value={v.toLowerCase()}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group-custom half checkbox-align">
                <label className="checkbox-container-label">
                  <input
                    type="checkbox"
                    checked={scriptForm.isDefault}
                    onChange={(e) => setScriptForm((f) => ({ ...f, isDefault: e.target.checked }))}
                  />
                  <span>Set as default template</span>
                </label>
              </div>
            </div>

            {formError && <div className="form-error-alert-label">{formError}</div>}

            <div className="form-actions-buttons-row">
              <button
                className="form-action-btn secondary"
                onClick={() => { setEditingScriptId(null); setIsWideView(false); }}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button className="form-action-btn primary" onClick={saveScript} disabled={formSaving}>
                {formSaving ? 'Saving...' : editingScriptId === '__new__' ? 'Create Script' : 'Update Script'}
              </button>
            </div>
          </div>
        ) : (
          <div className="form-empty-state-card">
            <span className="empty-state-icon">📝</span>
            <p>Select a script template on the left to edit or configuration parameters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptTemplates;
